import React, { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import Cropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BASE_URL, { IMAGE_DOMAIN } from '../../config/config';
import { Link } from 'react-router-dom';

// Recortar píxeles de un canvas a partir del área visible
const getCroppedImg = async (imageSrc, pixelCrop) => {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', reject);
    img.src = imageSrc;
  });
  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y,
    pixelCrop.width, pixelCrop.height,
    0, 0,
    pixelCrop.width, pixelCrop.height
  );
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/webp', 0.85);
  });
};

const TOTAL_STEPS = 4;

const EmpiezaAhora = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    usuario: '',
    contrasena: '',
    confirmarContrasena: '',
    nombre: '',
    apellidos: '',
    telefono: '',
    email: '',
    webs_del_cliente: '',
  });
  const [fotoFile, setFotoFile] = useState(null);       // Blob recortado final
  const [showPass, setShowPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // ── Crop state ──────────────────────────────────────────────────────────────
  const [rawImageSrc, setRawImageSrc] = useState(null);  // data-URL de la img original
  const [previewUrl, setPreviewUrl] = useState(null);     // data-URL del recorte final
  const [showCropper, setShowCropper] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const fileInputRef = useRef(null);
  // ────────────────────────────────────────────────────────────────────────────

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Abrir cropper al seleccionar archivo
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setRawImageSrc(reader.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
    // Reset input para permitir reseleccionar el mismo archivo
    e.target.value = '';
  };

  const onCropComplete = useCallback((_area, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const aplicarRecorte = async () => {
    try {
      const blob = await getCroppedImg(rawImageSrc, croppedAreaPixels);
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setFotoFile(blob);
      setShowCropper(false);
    } catch {
      toast.error('No se pudo recortar la imagen.');
    }
  };

  const cancelarRecorte = () => {
    setShowCropper(false);
    setRawImageSrc(null);
  };

  const quitarFoto = () => {
    setPreviewUrl(null);
    setFotoFile(null);
    setRawImageSrc(null);
  };

  // ── Errores ─────────────────────────────────────────────────────────────────
  const showError = (message) => {
    toast.error(message, {
      position: 'bottom-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  // ── Validaciones ─────────────────────────────────────────────────────────────
  const validateStep = (currentStep) => {
    if (currentStep === 1) {
      const usuarioRegex = /^[a-z0-9_]+$/;
      if (!usuarioRegex.test(formData.usuario)) {
        showError('El nombre de usuario solo acepta letras minúsculas, números y guiones bajos.');
        return false;
      }
      if (formData.usuario.length < 3) {
        showError('El nombre de usuario debe tener al menos 3 caracteres.');
        return false;
      }
      if (formData.contrasena.length < 5) {
        showError('La contraseña debe tener al menos 5 caracteres.');
        return false;
      }
      if (formData.contrasena !== formData.confirmarContrasena) {
        showError('Las contraseñas no coinciden.');
        return false;
      }
    }
    if (currentStep === 2) {
      if (!formData.nombre) { showError('El campo nombre no puede estar vacío.'); return false; }
      if (!formData.apellidos) { showError('El campo apellidos no puede estar vacío.'); return false; }
    }
    if (currentStep === 3) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) { showError('Por favor, introduce un email válido.'); return false; }
      const telefonoRegex = /^[0-9+()/\-\s]*$/;
      if (!formData.telefono) { showError('El campo de teléfono no puede estar vacío.'); return false; }
      if (!telefonoRegex.test(formData.telefono)) { showError('El teléfono solo puede contener números y los caracteres +, (, ), -, /.'); return false; }
      if (!formData.webs_del_cliente) { showError('El campo de webs no puede estar vacío.'); return false; }
    }
    return true;
  };

  const changeStep = (newStep) => {
    if (newStep > step && !validateStep(step)) return;
    setStep(newStep);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Avanzar con Enter en el último input de cada paso
  const handleKeyDown = (e, isLastInput) => {
    if (e.key === 'Enter' && isLastInput) {
      e.preventDefault();
      if (step < TOTAL_STEPS) changeStep(step + 1);
    }
  };

  // ── Envío ────────────────────────────────────────────────────────────────────
  const notificarAdministrador = async () => {
    try {
      await axios.post(`${BASE_URL}/notificar-registro`, {
        usuario: formData.usuario,
        nombre: formData.nombre,
        apellidos: formData.apellidos,
        webs_aseguradas: formData.webs_del_cliente,
      });
    } catch (error) {
      console.error('Error al enviar la notificación:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(3)) return;
    setLoading(true);

    const formDataToSend = new FormData();
    Object.keys(formData).forEach((key) => {
      formDataToSend.append(key, formData[key] || '');
    });
    if (fotoFile) {
      formDataToSend.append('fotoPerfil', fotoFile, 'avatar.webp');
    }

    try {
      await axios.post(`${BASE_URL}/registrar-usuario`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess(true);
      await notificarAdministrador();
    } catch (err) {
      const msg = err.response?.data?.message || 'Error al crear el usuario. Inténtalo de nuevo.';
      showError(msg);
    } finally {
      setLoading(false);
    }
  };

  const progressPercentage = (step / TOTAL_STEPS) * 100;

  // ── Indicador de pasos ───────────────────────────────────────────────────────
  const stepLabels = ['Acceso', 'Datos', 'Contacto', 'Foto'];

  return (
    <main className="site-width">
      <ToastContainer position="bottom-right" autoClose={3000} />

      {/* Modal de recorte */}
      {showCropper && (
        <div className="crop-overlay">
          <div className="crop-modal">
            <div className="crop-header">
              <h3>Ajusta tu foto de perfil</h3>
              <p>Centra y recorta la imagen a tu gusto</p>
            </div>
            <div className="crop-area">
              <Cropper
                image={rawImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className="crop-zoom-row">
              <span>Zoom</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="crop-zoom-slider"
              />
            </div>
            <div className="crop-actions">
              <button type="button" className="crop-btn-cancel" onClick={cancelarRecorte}>Cancelar</button>
              <button type="button" className="crop-btn-apply" onClick={aplicarRecorte}>Aplicar recorte</button>
            </div>
          </div>
        </div>
      )}

      {success ? (
        <div className="verificar-card-registro">
          <img src="/images/exito.svg" alt="Icono" />
          <h2>¡Registro completado con éxito!</h2>
          <p>
            Estás a un solo paso de acceder al soporte.<br /><br />
            Nuestros técnicos están validando tu información y pronto verificaremos tu cuenta.<br />
            Puedes consultar el estado iniciando sesión con tus credenciales.
          </p>
          <Link to="/iniciar-sesion" className="boton-registro-completado">Consultar estado</Link>
        </div>
      ) : (
        <div className="grid-registrarse">

          {/* Cabecera */}
          <div className="registro-header">
            <h1>Empieza ahora</h1>
            <p>Crea tu cuenta de cliente para recibir soporte técnico en unos simples pasos.</p>
          </div>

          {/* Indicador de pasos */}
          <div className="step-indicator">
            {stepLabels.map((label, i) => {
              const num = i + 1;
              const isCompleted = num < step;
              const isCurrent = num === step;
              return (
                <React.Fragment key={num}>
                  <div className={`step-dot-wrapper ${isCurrent ? 'current' : ''} ${isCompleted ? 'completed' : ''}`}>
                    <div className="step-dot">
                      {isCompleted ? <span className="step-check">✓</span> : <span>{num}</span>}
                    </div>
                    <span className="step-dot-label">{label}</span>
                  </div>
                  {i < stepLabels.length - 1 && (
                    <div className={`step-connector ${isCompleted ? 'filled' : ''}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          <form onSubmit={handleSubmit}>

            {/* ── PASO 1 ── */}
            {step === 1 && (
              <div className="grid-step">
                <div className="content-step">
                  <h2>Paso 1: Acceso al soporte</h2>
                  <p>Con estas credenciales accederás a tu panel de soporte.</p>
                </div>
                <div className="step-group">
                  <label>
                    Nombre de usuario
                    <span>Solo letras minúsculas, números y guiones bajos. (Ej: agenciaadhoc)</span>
                    <div className="campo-de-usuario-registro">
                      <img src="/images/arroba.svg" alt="Usuario" />
                      <input
                        type="text"
                        name="usuario"
                        placeholder="johndoe"
                        value={formData.usuario}
                        onChange={handleInputChange}
                        onKeyDown={(e) => handleKeyDown(e, false)}
                        required
                      />
                    </div>
                  </label>
                  <label>
                    Contraseña
                    <div className="input-with-show-pass">
                      <input
                        id="contrasena"
                        type={showPass ? 'text' : 'password'}
                        name="contrasena"
                        value={formData.contrasena}
                        onChange={handleInputChange}
                        onKeyDown={(e) => handleKeyDown(e, false)}
                        autoComplete="new-password"
                        placeholder="Mínimo 5 caracteres"
                        required
                      />
                      <button type="button" className="show-pass-visibility" onClick={() => setShowPass(v => !v)} tabIndex="-1">
                        <img src={showPass ? '/images/ojo-cerrado.svg' : '/images/ojo-abierto.svg'} alt="" />
                      </button>
                    </div>
                  </label>
                  <label>
                    Confirmar contraseña
                    <div className="input-with-show-pass">
                      <input
                        id="confirmarContrasena"
                        type={showConfirmPass ? 'text' : 'password'}
                        name="confirmarContrasena"
                        value={formData.confirmarContrasena}
                        onChange={handleInputChange}
                        onKeyDown={(e) => handleKeyDown(e, true)}
                        autoComplete="new-password"
                        placeholder="Repite tu contraseña"
                        required
                      />
                      <button type="button" className="show-pass-visibility" onClick={() => setShowConfirmPass(v => !v)} tabIndex="-1">
                        <img src={showConfirmPass ? '/images/ojo-cerrado.svg' : '/images/ojo-abierto.svg'} alt="" />
                      </button>
                    </div>
                  </label>
                </div>
                <div className="grid-step-buttons single-right">
                  <button type="button" className="btn-step-next" onClick={() => changeStep(2)}>Siguiente →</button>
                </div>
              </div>
            )}

            {/* ── PASO 2 ── */}
            {step === 2 && (
              <div className="grid-step">
                <div className="content-step">
                  <h2>Paso 2: Datos personales</h2>
                  <p>¿Cómo nos dirigimos hacia ti?</p>
                </div>
                <div className="step-group">
                  <label>
                    Nombre
                    <input
                      type="text"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleInputChange}
                      onKeyDown={(e) => handleKeyDown(e, false)}
                      placeholder="Ej: John"
                      required
                    />
                  </label>
                  <label>
                    Apellidos
                    <input
                      type="text"
                      name="apellidos"
                      value={formData.apellidos}
                      onChange={handleInputChange}
                      onKeyDown={(e) => handleKeyDown(e, true)}
                      placeholder="Ej: Doe"
                      required
                    />
                  </label>
                </div>
                <div className="grid-step-buttons">
                  <button type="button" className="btn-step-prev" onClick={() => changeStep(1)}>← Anterior</button>
                  <button type="button" className="btn-step-next" onClick={() => changeStep(3)}>Siguiente →</button>
                </div>
              </div>
            )}

            {/* ── PASO 3 ── */}
            {step === 3 && (
              <div className="grid-step">
                <div className="content-step">
                  <h2>Paso 3: Información de contacto</h2>
                  <p>Con estos datos podemos contactarte y asociar tus servicios contratados.</p>
                </div>
                <div className="step-group">
                  <label>
                    Email
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onKeyDown={(e) => handleKeyDown(e, false)}
                      placeholder="micorreo@dominio.com"
                      required
                    />
                  </label>
                  <label>
                    Teléfono
                    <span>Incluye tu código de país. (Ej: +34 600 00 00 00)</span>
                    <input
                      type="tel"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleInputChange}
                      onKeyDown={(e) => handleKeyDown(e, false)}
                      placeholder="+34 600 00 00 00"
                      required
                    />
                  </label>
                  <label>
                    Páginas web en mantenimiento
                    <span>Si tienes más de una, sepáralas con comas. (Ej: dominio1.com, dominio2.com)</span>
                    <input
                      type="text"
                      name="webs_del_cliente"
                      value={formData.webs_del_cliente}
                      onChange={handleInputChange}
                      onKeyDown={(e) => handleKeyDown(e, true)}
                      placeholder="dominio1.com, dominio2.com"
                      required
                    />
                  </label>
                </div>
                <div className="grid-step-buttons">
                  <button type="button" className="btn-step-prev" onClick={() => changeStep(2)}>← Anterior</button>
                  <button type="button" className="btn-step-next" onClick={() => changeStep(4)}>Siguiente →</button>
                </div>
              </div>
            )}

            {/* ── PASO 4 ── */}
            {step === 4 && (
              <div className="grid-step">
                <div className="content-step">
                  <h2>Paso 4: Foto de perfil <span className="opcional">(Opcional)</span></h2>
                  <p>Todos nuestros técnicos usan su foto. Si no subes ninguna, te asignaremos un avatar.</p>
                </div>

                <div className="foto-perfil-uploader">
                  {previewUrl ? (
                    <div className="foto-preview-wrap">
                      <img src={previewUrl} alt="Vista previa" className="foto-preview-circle" />
                      <div className="foto-preview-actions">
                        <button type="button" className="btn-foto-change" onClick={() => fileInputRef.current?.click()}>
                          Cambiar foto
                        </button>
                        <button type="button" className="btn-foto-remove" onClick={quitarFoto}>
                          Quitar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="foto-upload-placeholder" onClick={() => fileInputRef.current?.click()}>
                      <div className="foto-upload-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M12 16v-4m0 0V8m0 4H8m4 0h4" strokeLinecap="round" />
                          <circle cx="12" cy="12" r="10" />
                        </svg>
                      </div>
                      <span className="foto-upload-text">Haz clic para subir una foto</span>
                      <span className="foto-upload-hint">JPG, PNG o WEBP · Máx. 5 MB</span>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </div>

                <div className="grid-step-buttons">
                  <button type="button" className="btn-step-prev" onClick={() => changeStep(3)}>← Anterior</button>
                  <button type="submit" className="btn-step-submit" disabled={loading}>
                    {loading ? (
                      <span className="btn-loading">
                        <span className="btn-spinner" /> Enviando...
                      </span>
                    ) : 'Completar registro'}
                  </button>
                </div>
              </div>
            )}

          </form>
        </div>
      )}
    </main>
  );
};

export default EmpiezaAhora;
